import axios from 'axios'
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import * as XLSX from 'xlsx';
import '../styles/general.css'
import Chart from 'react-apexcharts';
import { useDispatch, useSelector } from 'react-redux'
import { roundy } from './Utilities'
import { Spinner } from 'react-bootstrap'
import LoginModal from "../components/LoginModal"
import RegisterModal from "../components/RegisterModal"
import ForgotModal from "../components/ForgotModal"
import AddFolderModal from "../components/AddFolderModal"
import MessageModal from "../components/MessageModal"
import {
    // updateCard,
    setIndicator,
} from '../actions/searchActions'
import {
    getFolders,
} from '../actions/platformActions'
import { saveSeries } from '../actions/viewerActions'
import ViewerModal from '../components/ViewerModal'
import { SELECTED_INDICATOR } from '../constants/searchConstants'
import { SAVE_SERIES_RESET } from '../constants/viewerConstants'
import { includesCountry } from '../utils/countryNames'
import { getFlagFallback, getFlagUrl } from '../utils/countryFlags'
import { formatDateAxisLabel, formatEffectiveUnitLabel, getEffectiveUnit } from '../utils/chartLabels'
import { getUnitsInOrder, sortUnitsForDisplay } from '../utils/seriesOptions'
import { getTransformationHelp, getTransformationLabel, getTransformationShortLabel } from '../utils/transformations'
import YearRangeSlider from './YearRangeSlider'

const defaultColors = [
    '#0073CF',
    '#7209B7',
    '#D62828',
    '#06A77D',
    '#F5A623',
    '#00B4D8',
    '#164773',
    '#3B8C6E',
    '#D94F04',
    '#42515C',
    '#89D99D',
    '#323050',
];

const VIEWER_HASH_MARKER = 'viewer=open'

const formatChartNumber = (value, maximumFractionDigits = 2) => {
    if (value === null || value === undefined || isNaN(value)) return ''
    return Number(value).toLocaleString('en-US', {
        maximumFractionDigits,
    })
}

function NoteCountryFlag({ country }) {
    const flagUrl = getFlagUrl(country)

    return (
        <span className="sc-note-flag" title={country} aria-label={country}>
            {flagUrl && (
                <img
                    src={flagUrl}
                    alt={`${country} flag`}
                    loading="lazy"
                    width="20"
                    height="15"
                    onError={event => {
                        event.currentTarget.style.display = 'none'
                        event.currentTarget.nextSibling.style.display = 'inline-flex'
                    }}
                />
            )}
            <span
                className="sc-note-flag-fallback"
                style={{ display: flagUrl ? 'none' : 'inline-flex' }}
            >
                {getFlagFallback(country)}
            </span>
        </span>
    )
}

function VariationHelp({ transformations }) {
    const [isPinned, setIsPinned] = useState(false)
    const containerRef = useRef(null)
    const panelId = useId()
    const transformationKey = transformations.join('|')

    useEffect(() => {
        setIsPinned(false)
    }, [transformationKey])

    useEffect(() => {
        if (!isPinned) return undefined

        const closeOnOutsideClick = event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsPinned(false)
            }
        }
        const closeOnEscape = event => {
            if (event.key === 'Escape') setIsPinned(false)
        }

        document.addEventListener('mousedown', closeOnOutsideClick)
        document.addEventListener('keydown', closeOnEscape)
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick)
            document.removeEventListener('keydown', closeOnEscape)
        }
    }, [isPinned])

    return (
        <div
            className={`sc-variation-help${isPinned ? ' is-pinned' : ''}`}
            ref={containerRef}
        >
            <button
                type="button"
                className="sc-variation-help-btn"
                onClick={() => setIsPinned(pinned => !pinned)}
                aria-label="About available variations"
                aria-expanded={isPinned}
                aria-controls={panelId}
                aria-describedby={panelId}
            >
                <i className="fa-solid fa-circle-info" aria-hidden="true" />
            </button>
            <div
                id={panelId}
                className="sc-variation-help-panel"
                role={isPinned ? 'dialog' : 'tooltip'}
                aria-label="Available variation definitions"
            >
                <div className="sc-variation-help-header">
                    <strong>Available variations</strong>
                    <button
                        type="button"
                        className="sc-variation-help-close"
                        onClick={() => setIsPinned(false)}
                        aria-label="Close variation help"
                        aria-hidden={!isPinned}
                        tabIndex={isPinned ? 0 : -1}
                    >
                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                </div>
                <dl className="sc-variation-help-list">
                    {transformations.map(transformation => {
                        const help = getTransformationHelp(transformation)
                        return (
                            <div className="sc-variation-help-item" key={transformation}>
                                <dt>{help.label}</dt>
                                <dd>{help.description}</dd>
                            </div>
                        )
                    })}
                </dl>
            </div>
        </div>
    )
}

function SearchCard({ meta, data, countries, onFrequencyChange, defaultUnit, defaultTransformation }) {
    //console.log('metaCard: ', meta)
    //console.log('countries: ', countries)
    //console.log('data: ', data)


    const dispatch = useDispatch()

    const [messageModal, setMessageModal] = useState('')
    const [modalTitle, setModalTitle] = useState('')
    const [modalMessage, setModalMessage] = useState('')

    const [saveType, setSaveType] = useState('')

    // const [viewerMeta, setViewerMeta] = useState(meta)
    // setCountryValue(prevCountries => [...prevCountries, { id: prevCountries.length, value: '', label: '', color: defaultColors[prevCountries.length], type: 'line', countryType: ''}])

    const userLogin = useSelector(state => state.userLogin)
    const { userInfo } = userLogin

    useEffect(() => {
        if (userInfo) {
            dispatch(getFolders())
        }
    }, [dispatch, userInfo])

    const [metaCountry, setMetaCountry] = useState([])
    const [isExpanded, setIsExpanded] = useState(false)

    // let finalMeta = JSON.parse(JSON.stringify(meta))
    // finalMeta.chart = {
    //     // unit: {value: meta.units[0], label: meta.units[0]},
    //     unit: {value: unitValue, label: unitValue},
    //     frequency: {value: meta.frequency, label: meta.frequency},
    //     country: metaCountry,
    //     legend: true,
    //     metrics: [{id: 0, value: '', label: ''}],
    //     xaxis: [{id: 0, value: '', label: ''}],
    //     yaxis: [{id: 0, value: '', label: '', text:''}],
    //     highlight: [{
    //         id: 0,
    //         x1: {value: '', label: ''},
    //         x2: {value: '', label: ''},
    //     }]
    // }


    const [loginModal, setLoginModal] = useState(false)
    const [registerModal, setRegisterModal] = useState(false)
    const [forgotModal, setForgotModal] = useState(false)

    const [viewerModal, setViewerModal] = useState(false)
    const viewerHistoryPushedRef = useRef(false)
    const [addFolderModal, setAddFolderModal] = useState(false)
    
    // Estado para loading cuando cambia la frecuencia
    const [loadingFrequency, setLoadingFrequency] = useState(false)

    // const [metaSender, setMetaSender] = useState(finalMeta)

    const [realData, setRealData] = useState(data || [])
    // Memoized because its identity feeds the resolvedSelection memo chain: a new
    // array on every render retriggers the effect below and loops the component.
    const availableUnitsFromMeta = useMemo(() => sortUnitsForDisplay(meta.units), [meta.units])
    const initialUnitsFromData = getUnitsInOrder(data)
    const initialUnits = initialUnitsFromData.length > 0 ? initialUnitsFromData : availableUnitsFromMeta
    // Keep the first unit exactly as supplied by the data unless a valid default is explicit.
    const validDefaultUnit = (defaultUnit && initialUnits.includes(defaultUnit)) ? defaultUnit : (initialUnits[0] || '')
    const [unitValue, setUnitValue] = useState(validDefaultUnit)
    const [frequencyValue, setFrequencyValue] = useState(meta.frequency)
    const [definitionValue, setDefinitonValue] = useState(meta.units_definitions?.[validDefaultUnit] || '')
    const [numCountries, setNumCountries] = useState([...new Set((data || []).map(item => item.country))].length)
    const [selectedContries, setSelectedCountries] = useState([...new Set((data || []).map(item => item.country))])
    const [sourcesList, setSourcesList] = useState([...new Set((data || []).map(item => item.source))])
    
    // Usar available_frequencies del backend (desde IndicatorIdRange)
    // Si no está disponible, usar other_frequencies como fallback
    const getInitialFrequencies = () => {
        // Primero intentar usar available_frequencies del backend
        if (meta.available_frequencies && meta.available_frequencies.length > 0) {
            // Filtrar la frecuencia actual y devolver las otras
            const otherFreqs = meta.available_frequencies.filter(f => f !== meta.frequency)
            return otherFreqs.length > 0 ? otherFreqs.join(',') : meta.frequency
        }
        
        // Fallback a other_frequencies
        if (!meta.other_frequencies || meta.other_frequencies.length === 0) {
            return meta.frequency || 'Annual'
        }
        const validFrequencies = meta.other_frequencies.filter(f => f && f.trim())
        if (validFrequencies.length === 0) {
            return meta.frequency || 'Annual'
        }
        return validFrequencies.reduce((a, b) => a.length > b.length ? a : b).replace('and', ',')
    }
    const [otherFrequenciesList, setOtherFrequencyList] = useState(getInitialFrequencies())
    const [xdata, setXdata] = useState([])
    const [ydata, setYdata] = useState([])
    const [minDate, setMinDate] = useState(null)
    const [maxDate, setMaxDate] = useState(null)
    const [showNotes, setShowNotes] = useState(false)
    const [yearRange, setYearRange] = useState('max')
    const [fromYear, setFromYear] = useState('')
    const [toYear, setToYear] = useState('')
    const [chartType, setChartType] = useState('line')
    
    // Estado para transformaciones disponibles (dependen de indicator+country+unit)
    // transformations_by_unit contiene las transformaciones agrupadas por unidad
    const transformationsByUnit = meta.transformations_by_unit || {}
    
    // Obtener transformaciones para la unidad actual
    const getTransformationsForUnit = useCallback((unit, dataset = realData) => {
        if (!unit) return []

        const values = new Set()
        ;(dataset || []).forEach(item => {
            if (item.unit !== unit) return
            const transformation = String(item.transformation || '').trim()
            if (transformation) values.add(transformation)
        })

        if (values.size > 0) {
            return Array.from(values).sort()
        }

        if (transformationsByUnit && transformationsByUnit[unit]) {
            return transformationsByUnit[unit]
        }

        return []
    }, [realData, transformationsByUnit])
    
    const initialTransformations = getTransformationsForUnit(validDefaultUnit, data || [])
    // Validate defaultTransformation against what's actually available for the valid unit
    const validDefaultTransformation = (defaultTransformation && initialTransformations.includes(defaultTransformation))
        ? defaultTransformation
        : ''
    const [selectedTransformation, setSelectedTransformation] = useState(validDefaultTransformation)

    // Obtener notas concatenadas (únicas)
    const notesList = meta.notes || []
    const notesText = notesList.length > 0 ? notesList.join(' | ') : ''

    // meta.notes llega deduplicado y sin país, así que el país de cada nota se
    // resuelve desde las filas crudas, que sí conservan ambos campos.
    const countriesByNote = useMemo(() => {
        const map = new Map()
        ;(realData || []).forEach(item => {
            const note = String(item.notes || '').trim()
            const country = String(item.country || '').trim()
            if (!note || !country) return
            if (!map.has(note)) map.set(note, new Set())
            map.get(note).add(country)
        })
        return map
    }, [realData])

    const getNoteCountries = useCallback(note => (
        Array.from(countriesByNote.get(String(note).trim()) || []).sort()
    ), [countriesByNote])

    // Obtener la etiqueta de transformación
    const transformationValue = meta.transformation || ''
    const transformationLabel = getTransformationLabel(transformationValue)

    // Preserve the unit order supplied by the current dataset.
    const availableUnitsForCurrentData = useMemo(() => {
        const unitsInData = getUnitsInOrder(realData)
        return unitsInData.length > 0 ? unitsInData : availableUnitsFromMeta
    }, [availableUnitsFromMeta, realData])

    const orderedUnits = availableUnitsForCurrentData

    useEffect(() => {
        setRealData(data || [])
    }, [data])

    const filterSeriesData = useCallback((dataset, unit, transformation) => {
        return (dataset || []).filter(item => {
            if (item.unit !== unit) return false

            const currentTransformation = String(item.transformation || '').trim()
            const requestedTransformation = String(transformation || '').trim()

            if (!requestedTransformation) {
                return currentTransformation === ''
            }

            return currentTransformation === requestedTransformation
        })
    }, [])

    const resolvedSelection = useMemo(() => {
        const candidateUnits = availableUnitsForCurrentData

        let resolvedUnit = candidateUnits.includes(unitValue) ? unitValue : (candidateUnits[0] || '')
        let transformationsForUnit = getTransformationsForUnit(resolvedUnit)
        let resolvedTransformation = transformationsForUnit.includes(selectedTransformation) ? selectedTransformation : ''
        let filtered = filterSeriesData(realData, resolvedUnit, resolvedTransformation)

        if (filtered.length === 0 && resolvedTransformation) {
            resolvedTransformation = ''
            filtered = filterSeriesData(realData, resolvedUnit, resolvedTransformation)
        }

        if (filtered.length === 0) {
            for (const candidateUnit of candidateUnits) {
                const candidateTransformations = getTransformationsForUnit(candidateUnit)
                const fallbackWithoutTransformation = filterSeriesData(realData, candidateUnit, '')

                if (fallbackWithoutTransformation.length > 0) {
                    resolvedUnit = candidateUnit
                    transformationsForUnit = candidateTransformations
                    resolvedTransformation = ''
                    filtered = fallbackWithoutTransformation
                    break
                }

                const firstValidTransformation = candidateTransformations.find(transformation =>
                    filterSeriesData(realData, candidateUnit, transformation).length > 0
                )

                if (firstValidTransformation) {
                    resolvedUnit = candidateUnit
                    transformationsForUnit = candidateTransformations
                    resolvedTransformation = firstValidTransformation
                    filtered = filterSeriesData(realData, candidateUnit, firstValidTransformation)
                    break
                }
            }
        }

        return {
            unit: resolvedUnit,
            transformation: resolvedTransformation,
            availableTransformations: transformationsForUnit,
            filteredData: filtered,
        }
    }, [availableUnitsForCurrentData, filterSeriesData, getTransformationsForUnit, realData, selectedTransformation, unitValue])

    const effectiveUnitValue = getEffectiveUnit(resolvedSelection.filteredData, resolvedSelection.unit)
    const availableTransformations = resolvedSelection.availableTransformations

    let finalMeta = JSON.parse(JSON.stringify(meta))
    finalMeta.chart = {
        unit: { value: unitValue, label: unitValue },
        effectiveUnit: effectiveUnitValue,
        transformation: {
            value: selectedTransformation,
            label: getTransformationLabel(selectedTransformation),
        },
        frequency: { value: frequencyValue, label: frequencyValue },
        country: metaCountry,
        legend: true,
        metrics: [{ id: 0, value: '', label: '' }],
        xaxis: [{ id: 0, value: '', label: '' }],
        yaxis: [{ id: 0, value: '', label: '', text: '' }],
        highlight: [{
            id: 0,
            x1: { value: '', label: '' },
            x2: { value: '', label: '' },
        }],
        yearRange,
        fromYear,
        toYear,
    }
    finalMeta.chart.colors = defaultColors

    const [metaSender, setMetaSender] = useState(finalMeta)

    useEffect(() => {
        if (resolvedSelection.unit !== unitValue) {
            setUnitValue(resolvedSelection.unit)
        }

        if (resolvedSelection.transformation !== selectedTransformation) {
            setSelectedTransformation(resolvedSelection.transformation)
        }

        setDefinitonValue(meta.units_definitions?.[resolvedSelection.unit] || '')
        setSelectedCountries([...new Set((resolvedSelection.filteredData || []).map(item => item.country))])
        setSourcesList([...new Set((resolvedSelection.filteredData || []).map(item => item.source))])
    }, [meta.units_definitions, resolvedSelection, selectedTransformation, unitValue])

    // Paso 1: Agrupar los datos por país
    useEffect(() => {
        let general = resolvedSelection.filteredData

        const groupedData = general.reduce((acc, curr) => {
            if (!acc[curr.country]) {
                acc[curr.country] = [];
            }
            acc[curr.country].push({ date: curr.date, value: curr.value });
            return acc;
        }, {});

        if (general.length > 0) {
            // Paso 2: Obtener una lista de todas las dates únicas en orden ascendente
            const x = Array.from(new Set(general.map(item => item.date))).sort();
            setXdata(x)
            //console.log("xdata: ", x)
            setMinDate(x[0].split('-')[0])
            setMaxDate(x[x.length - 1].split('-')[0])


            // Paso 3: Construir los ydata para cada país con null en las dates sin valores
            var y = Object.entries(groupedData).map(([country, values]) => {
                const ydata = x.map(date => {
                    const valueObj = values.find(item => item.date === date);

                    if (!valueObj) return null;

                    const parsed = parseFloat(valueObj.value);

                    return isNaN(parsed) ? null : parsed;
                });
                return { name: country, data: ydata };
            });

            y = y.sort((a, b) => a.name.localeCompare(b.name))

            setYdata(y)
            //   console.log("ydata::::: ", y)
            setNumCountries([...new Set(general.map(item => item.country))].length)
        }
        else {
            setXdata([])
            setYdata([{
                name: 'no data',
                data: []
            }])
            setMinDate('')
            setMaxDate('')
            setNumCountries(0)
        }

    }, [resolvedSelection.filteredData])

    // Manejar cambios en countries por separado
    useEffect(() => {
        if (countries) {
            setMetaCountry(countries?.map((item, index) => {
                return {
                    id: index,
                    value: item.label,
                    label: item.label,
                    color: defaultColors[index],
                    type: 'line',
                    countryType: 'country'
                }
            }))
        } else {
            setMetaCountry([])
        }
    }, [countries])

    const options = {
        chart: {
            id: 'basic-line-chart',
            toolbar: {
                show: false,
            },
            dropShadow: {
                enabled: false,
            },
        },
        stroke: {
            width: 3,
        },
        colors: ['#5171E0', '#5FB2CD', '#FFB300'],
        xaxis: {
            tickAmount: 4,
            labels: {
                show: true,
                rotate: 0,
                formatter: (value) => {
                    if (value == null) return '';
                    const [a, b] = String(value).split('-').map(s => s.trim());
                    return b ? `${a} - ${b}` : a;  // <- evita “-undefined”
                },
                style: {
                    fontSize: '9px',
                    fontFamily: 'Open Sans',
                }
            },
            axisBorder: { show: true },
            axisTicks: { show: false },
            categories: xdata.map(item =>
                frequencyValue === "Annual"
                    ? String(item).split('-')[0].trim()
                    : String(item)
            ),
        },
        yaxis: {
            show: true,
            labels: {
                show: false
            },
            axisBorder: {
                show: true
            }
        },
        grid:
        {
            show: false,
        },
        legend: {
            position: 'top',
            fontFamily: 'Open Sans',
            horizontalAlign: 'left',
            markers: {
                size: 5
            }
        },
        tooltip: {
            y: {
                formatter: function (value) {
                    // Si el valor es null, undefined, NaN o 0, mostrar "N/A"
                    if (value === null || value === undefined || isNaN(value)) {
                        return 'N/A'
                    }
                    return formatChartNumber(value, 2)
                }
            }
        }
    };


    const series = ydata.slice(0, 3)

    // const changeData = useSelector(state => state.cardUpdate)
    // const {cardInfo} = changeData

    // useEffect(() => {
    //     if(cardInfo){
    //         setRealData(cardInfo.data_dict)
    //     }
    // }, [changeData])


    const changeUnitHandler = async (unit) => {
        setUnitValue(unit)
        setSelectedTransformation('')
    }
    
    const changeTransformationHandler = (transformation) => {
        setSelectedTransformation(transformation)
        // Aquí podrías agregar lógica adicional si necesitas filtrar datos por transformación
    }

    const changeFrequencyHandler = async (frequency) => {
        setLoadingFrequency(true)
        
        let a = otherFrequenciesList.split(',').filter(item => item != frequency)
        a.push(frequencyValue)
        let result = a.join(',')

        let freq = frequency.replaceAll(' ', '')
        freq = freq[0].toUpperCase() + freq.slice(1)
        const nextData = await updateCard(meta.topic, meta.indicator, meta.countries_used, unitValue, freq)

        setFrequencyValue(freq)
        setOtherFrequencyList(result)
        setRealData(nextData?.data_dict || [])

        // Notificar al componente padre sobre el cambio de frecuencia
        if (onFrequencyChange) {
            onFrequencyChange(freq)
        }
        
        setLoadingFrequency(false)
    }

    const downloadHandler = () => {
        try {
            // Verificar que hay datos para descargar
            if (!realData || realData.length === 0) {
                console.error('No hay datos para descargar');
                alert('No hay datos disponibles para descargar');
                return;
            }

            // Filtrar datos por la frecuencia y unidad actual
            const filteredData = realData.filter(item =>
                item.frequency.toLowerCase().trim() === frequencyValue.toLowerCase().trim() &&
                item.unit.toLowerCase().trim() === unitValue.toLowerCase().trim()
            );

            if (filteredData.length === 0) {
                console.error('No hay datos para la frecuencia y unidad seleccionadas');
                alert(`No hay datos disponibles para ${frequencyValue} - ${unitValue}`);
                return;
            }

            console.log('Descargando datos:', filteredData.length, 'registros', `(${frequencyValue}, ${unitValue})`);

            // Preparar los datos para el Excel
            // Usar 'year' de la nueva DB, con fallback a 'period' para compatibilidad
            const sortData = filteredData.map(item => ({
                indicator: meta.indicator,
                country: item.country,
                frequency: item.frequency,
                year: item.year || item.period || '',
                date: item.date,
                unit: item.unit,
                effective_unit: item.effective_unit,
                definition: item.definition,
                source: item.source,
                value: item.value,
                transformation: item.transformation || '',
                notes: item.notes || ''
            }));

            // Crear worksheet
            const worksheet = XLSX.utils.json_to_sheet(sortData);

            // Crear workbook y agregar la hoja
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

            // Generar nombre de archivo seguro
            const safeIndicatorName = meta.indicator.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
            const fileName = `${safeIndicatorName}-${frequencyValue}-${unitValue}.xlsx`;

            // Descargar el archivo
            XLSX.writeFile(workbook, fileName);

            console.log('Descarga completada:', fileName);
        } catch (error) {
            console.error('Error al descargar:', error);
            alert('Error al generar el archivo de descarga');
        }
    }
    const viewerHandler = () => {
        if (!window.location.hash.includes(VIEWER_HASH_MARKER)) {
            const separator = window.location.hash.includes('?') ? '&' : '?'
            window.history.pushState({ lmwViewer: true }, '', `${window.location.pathname}${window.location.search}${window.location.hash}${separator}${VIEWER_HASH_MARKER}`)
            viewerHistoryPushedRef.current = true
        }
        setViewerModal(true)
    }

    useEffect(() => {
        if (!viewerModal) return undefined

        const closeFromHistory = () => {
            if (!window.location.hash.includes(VIEWER_HASH_MARKER)) {
                viewerHistoryPushedRef.current = false
                setViewerModal(false)
            }
        }

        window.addEventListener('popstate', closeFromHistory)
        return () => window.removeEventListener('popstate', closeFromHistory)
    }, [viewerModal])

    // const setIndicator = async (indicator) => {
    //     dispatch({
    //         type: SELECTED_INDICATOR,
    //         payload: indicator
    //     })
    // }



    const folderInfo = useSelector(state => state.listFolder)
    const { loading: loadingFolder, error: errorFolder, folderList } = folderInfo

    // const folderCopy = folderList ? [...folderList]: []

    const folderOptions = folderList?.map(item => ({
        value: item.id,
        label: item.folder_name,
    }))

    const favoriteHandler = () => {

        if (userInfo) {
            setSaveType('favorite')
            dispatch({
                type: SAVE_SERIES_RESET,
            })
            dispatch(setIndicator(meta.indicator))
            let mainId = folderList?.filter(item => item.folder_name == 'Main Folder')[0].id
            // console.log(`default_${finalMeta.indicator}`)
            dispatch(saveSeries([mainId], finalMeta, finalMeta.indicator))
            // dispatch({
            //     type: SAVE_SERIES_RESET,
            // })
        } else {
            setLoginModal(true)
        }
    }

    const addFolderHandler = () => {
        if (userInfo) {
            setSaveType('folders')
            dispatch({
                type: SAVE_SERIES_RESET,
            })
            dispatch(setIndicator(meta.indicator))
            setMetaSender(finalMeta)
            setAddFolderModal(true)
        } else {
            setLoginModal(true)
        }
    }

    const saveInfo = useSelector(state => state.saveSeries)
    const { loading, error, success } = saveInfo

    const selectedIndicator = useSelector(state => state.selectedIndicator)
    const { indicator } = selectedIndicator

    useEffect(() => {
        if (success && indicator == meta.indicator) {
            setModalTitle('Saved')
            // console.log("saveType", saveType)
            if (saveType == 'favorite') {
                setModalMessage('The series has been successfully saved to the "Main Folder".')
            } else {
                setModalMessage('The series has been successfully in folders.')
            }
            setMessageModal(true)
            // dispatch({
            //     type: SAVE_SERIES_RESET,
            // })
        }
    }, [indicator, meta.indicator, saveInfo, saveType, success])

    const closeViewerHandler = () => {
        if (viewerHistoryPushedRef.current && window.location.hash.includes(VIEWER_HASH_MARKER)) {
            window.history.back()
            return
        }
        viewerHistoryPushedRef.current = false
        setViewerModal(false)
    }
    const closeFolderHandler = () => {
        setAddFolderModal(false)
        // dispatch({type: SAVE_SERIES_RESET})
    }
    const closeMessageHandler = () => {
        setMessageModal(false)
        setModalTitle('')
        setModalMessage('')
    }

    const updateCard = async (topic, indicator, country, unit, frequency) => {
        try {
            const config = {
                headers: {
                    'Content-type': 'application/json'
                }
            }

            const { data } = await axios.post(
                '/api/v1/data/change-card-data/', {
                topic: topic,
                indicator: indicator,
                country: country,
                unit: unit,
                frequency: frequency,
            },
                config
            )

            return data

        } catch (error) {
            console.log("error")
            return null
        }
    }

    // Obtener lista de todos los países disponibles para este indicador
    const availableCountriesList = meta.available_countries_list && meta.available_countries_list.length > 0
        ? meta.available_countries_list
        : [...new Set(realData.map(item => item.country))].sort()

    const allAvailableCountries = availableCountriesList.join(', ')

    // Filtrar selected countries para mostrar solo los que están disponibles para este indicador
    const selectedAvailableCountries = meta.countries_used && meta.countries_used.length > 0
        ? meta.countries_used.filter(country => includesCountry(availableCountriesList, country))
        : []

    const allFreqs = otherFrequenciesList
        ? otherFrequenciesList.split(',').map(f => f.trim()).filter(Boolean)
        : []
    const freqOptions = Array.from(new Set([frequencyValue, ...allFreqs]))

    const availableYears = useMemo(() => (
        Array.from(new Set(
            xdata
                .map(item => parseInt(String(item).split('-')[0], 10))
                .filter(year => !Number.isNaN(year))
        )).sort((a, b) => a - b)
    ), [xdata])

    useEffect(() => {
        if (availableYears.length === 0) {
            setFromYear('')
            setToYear('')
            return
        }

        // On the 'max' preset (the default, untouched state) always re-anchor to
        // the newest available year. Otherwise a frequency switch that still
        // contains the old toYear (e.g. Annual ending 2025 -> Quarterly having
        // data through 2026) leaves the slider stuck showing the stale year even
        // though the chart itself (which ignores fromYear/toYear in 'max' mode,
        // see getDisplayData below) already renders the newer data — only the
        // custom-range case should preserve the user's own picked bounds.
        if (yearRange === 'max') {
            setFromYear(String(availableYears[0]))
            setToYear(String(availableYears[availableYears.length - 1]))
            return
        }

        setFromYear(prev => prev && availableYears.includes(Number(prev)) ? prev : String(availableYears[0]))
        setToYear(prev => prev && availableYears.includes(Number(prev)) ? prev : String(availableYears[availableYears.length - 1]))
    }, [availableYears, yearRange])

    const applyAvailableRange = () => {
        if (availableYears.length === 0) return
        setYearRange('custom')
        setFromYear(String(availableYears[0]))
        setToYear(String(availableYears[availableYears.length - 1]))
    }

    const applyRecentAvailableRange = () => {
        if (availableYears.length === 0) return
        setYearRange('custom')
        setFromYear(String(availableYears[Math.max(0, availableYears.length - 10)]))
        setToYear(String(availableYears[availableYears.length - 1]))
    }

    const changeFromYearHandler = (value) => {
        setYearRange('custom')
        setFromYear(value)
        if (toYear && Number(value) > Number(toYear)) setToYear(value)
    }

    const changeToYearHandler = (value) => {
        setYearRange('custom')
        setToYear(value)
        if (fromYear && Number(value) < Number(fromYear)) setFromYear(value)
    }

    // Year range filtering for chart display
    const YEAR_RANGE_OPTIONS = [
        { value: '1', label: '1Y' },
        { value: '3', label: '3Y' },
        { value: '5', label: '5Y' },
        { value: '10', label: '10Y' },
        { value: 'max', label: 'Max' },
    ]
    const getDisplayData = () => {
        if (xdata.length === 0) return { x: xdata, y: ydata }
        if (yearRange === 'custom') {
            const from = Number(fromYear)
            const to = Number(toYear)
            if (!from || !to) return { x: xdata, y: ydata }

            const indexes = xdata.reduce((acc, item, index) => {
                const year = parseInt(String(item).split('-')[0], 10)
                if (!Number.isNaN(year) && year >= from && year <= to) acc.push(index)
                return acc
            }, [])

            if (indexes.length === 0) return { x: [], y: ydata.map(s => ({ name: s.name, data: [] })) }

            return {
                x: indexes.map(index => xdata[index]),
                y: ydata.map(s => ({ name: s.name, data: indexes.map(index => s.data[index]) }))
            }
        }
        if (yearRange === 'max') return { x: xdata, y: ydata }
        const years = parseInt(yearRange)
        const lastYear = parseInt(String(xdata[xdata.length - 1]).split('-')[0])
        const cutoffYear = lastYear - years
        const startIdx = xdata.findIndex(d => parseInt(String(d).split('-')[0]) > cutoffYear)
        if (startIdx <= 0) return { x: xdata, y: ydata }
        return {
            x: xdata.slice(startIdx),
            y: ydata.map(s => ({ name: s.name, data: s.data.slice(startIdx) }))
        }
    }
    const { x: displayX, y: displayY } = getDisplayData()

    const chartTickAmount = useMemo(() => {
        const totalPoints = displayX.length
        if (totalPoints <= 1) return 1

        if (frequencyValue === 'Monthly') {
            return Math.min(totalPoints - 1, 12)
        }

        if (frequencyValue === 'Quarterly') {
            return Math.min(totalPoints - 1, 8)
        }

        return Math.min(totalPoints - 1, 5)
    }, [displayX.length, frequencyValue])

    return (
        <div className='sc-card'>
            {/* Loading overlay */}
            {loadingFrequency && (
                <div className="sc-loading-overlay">
                    <div style={{ textAlign: 'center' }}>
                        <Spinner animation="border" style={{ color: '#0073CF', width: 24, height: 24 }} />
                    </div>
                </div>
            )}

            {/* ── Header ── */}
            <div className="sc-header">
                <h4 className="sc-title">{meta.indicator}</h4>
                <div className="sc-chips">
                    {meta.area && (
                        <span className="sc-chip">{meta.area.includes('_') ? meta.area.split('_')[1] : meta.area}</span>
                    )}
                    {meta.topic && (
                        <span className="sc-chip blue">{meta.topic.includes('_') ? meta.topic.split('_')[1] : meta.topic}</span>
                    )}
                    {minDate && maxDate && (
                        <span className="sc-chip" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                            {minDate}–{maxDate}
                        </span>
                    )}
                </div>
            </div>

            {/* ── Controls ── */}
            <div className="sc-controls">
                <div className="sc-control-group">
                    <span className="sc-control-label">Freq</span>
                    <select
                        className="sc-select"
                        value={frequencyValue}
                        onChange={e => changeFrequencyHandler(e.target.value)}
                        disabled={loadingFrequency}
                    >
                        {freqOptions.map(f => (
                            <option key={f} value={f}>
                                {f.charAt(0).toUpperCase() + f.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="sc-control-group">
                    <span className="sc-control-label">Unit</span>
                    <select
                        className="sc-select"
                        value={unitValue}
                        onChange={e => changeUnitHandler(e.target.value)}
                        disabled={orderedUnits.length <= 1}
                    >
                        {orderedUnits.map(u => (
                            <option key={u} value={u}>
                                {u.charAt(0).toUpperCase() + u.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="sc-controls-spacer" />

                <button className="sc-action-btn" onClick={downloadHandler} title="Download XLSX">
                    <i className="fa-solid fa-download" style={{ fontSize: 10 }} /> Download
                </button>
                <button className="sc-action-btn sc-viewer-btn" onClick={viewerHandler} title="Open full viewer">
                    <i className="fa-solid fa-expand" style={{ fontSize: 10 }} /> Viewer
                </button>
                <button
                    className="sc-action-btn"
                    onClick={favoriteHandler}
                    title="Save to favorites"
                >
                    <i className="fa-solid fa-star" style={{ fontSize: 10 }} />
                </button>
            </div>

            {availableTransformations.length > 0 && (
                <div className="sc-variation-bar">
                    <span className="sc-control-label">Variation</span>
                    <div className="sc-variation-options" role="group" aria-label="Variation">
                        {['', ...availableTransformations].map(transformation => (
                            <button
                                key={transformation || 'level'}
                                type="button"
                                className={`sc-time-btn sc-variation-btn${selectedTransformation === transformation ? ' active' : ''}`}
                                onClick={() => changeTransformationHandler(transformation)}
                                data-tooltip={getTransformationLabel(transformation)}
                                aria-label={`Variation: ${getTransformationLabel(transformation)}`}
                                aria-pressed={selectedTransformation === transformation}
                            >
                                {getTransformationShortLabel(transformation)}
                            </button>
                        ))}
                    </div>
                    <VariationHelp transformations={availableTransformations} />
                </div>
            )}

            {/* ── Chart ── */}
            {ydata.length > 0 && ydata[0].data.length > 0 ? (
                <div className="sc-chart">
                    {/* Year range selector */}
                    <div className="sc-chart-toolbar">
                        {/* Hidden, not removed — the year-range slider below the chart
                            already covers this same "pick a from/to year" job, so these
                            top controls (presets + From/To selects) are kept in the DOM
                            with their handlers/state intact but not shown. */}
                        <div className="sc-time-range" style={{ display: 'none' }}>
                            {YEAR_RANGE_OPTIONS.map(opt => (
                                <button
                                    key={opt.value}
                                    className={`sc-time-btn${yearRange === opt.value ? ' active' : ''}`}
                                    onClick={() => setYearRange(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <div className="sc-year-range-selectors" style={{ display: 'none' }}>
                            <span className="sc-control-label">From</span>
                            <select className="sc-select sc-year-select" value={fromYear} onChange={e => changeFromYearHandler(e.target.value)}>
                                {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                            </select>
                            <span className="sc-control-label">To</span>
                            <select className="sc-select sc-year-select" value={toYear} onChange={e => changeToYearHandler(e.target.value)}>
                                {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                            </select>
                            <button className="sc-time-btn" onClick={applyAvailableRange}>All years</button>
                            <button className="sc-time-btn" onClick={applyRecentAvailableRange}>Recent years</button>
                        </div>
                        <button
                            className="sc-action-btn sc-chart-type-btn"
                            onClick={() => setChartType(prev => prev === 'line' ? 'bar' : 'line')}
                            title="Toggle line/bar chart"
                        >
                            <i className={`fa-solid ${chartType === 'line' ? 'fa-chart-column' : 'fa-chart-line'}`} style={{ fontSize: 10 }} />
                            {chartType === 'line' ? 'Bars' : 'Lines'}
                        </button>
                    </div>
                    <Chart
                        options={{
                            chart: {
                                id: `sc-${meta.indicator}`,
                                toolbar: { show: false },
                                sparkline: { enabled: false },
                                animations: { enabled: true, speed: 400 },
                            },
                            stroke: { width: chartType === 'line' ? 2.5 : 0, curve: 'smooth' },
                            colors: defaultColors,
                            plotOptions: {
                                bar: {
                                    columnWidth: '70%',
                                    borderRadius: 2,
                                }
                            },
                            xaxis: {
                                tickAmount: chartTickAmount,
                                tickPlacement: 'on',
                                title: {
                                    text: 'Date',
                                    style: {
                                        fontSize: '11px',
                                        fontFamily: 'Open Sans, sans-serif',
                                        color: '#8b8b8b',
                                    }
                                },
                                categories: displayX.map(item =>
                                    frequencyValue === 'Annual'
                                        ? String(item).split('-')[0].trim()
                                        : String(item)
                                ),
                                labels: {
                                    rotate: 0,
                                    hideOverlappingLabels: false,
                                    showDuplicates: true,
                                    trim: false,
                                    style: {
                                        fontSize: frequencyValue === 'Monthly' ? '9px' : '10px',
                                        fontFamily: 'Open Sans, sans-serif',
                                        colors: '#737373'
                                    },
                                    formatter: v => {
                                        if (v == null) return ''
                                        const [a, b] = String(v).split('-').map(s => s.trim())
                                        return formatDateAxisLabel(b ? `${a}-${b}` : a)
                                    }
                                },
                                axisBorder: { show: true, color: '#e5e5e5' },
                                axisTicks: { show: true, color: '#d4d4d4' },
                            },
                            yaxis: {
                                title: {
                                    text: formatEffectiveUnitLabel(effectiveUnitValue),
                                    style: { fontSize: '11px', fontFamily: 'Open Sans, sans-serif', color: '#525252', fontWeight: 600 }
                                },
                                labels: {
                                    style: { fontSize: '10px', fontFamily: 'Open Sans, sans-serif', colors: '#737373' },
                                    formatter: v => formatChartNumber(v, 1)
                                }
                            },
                            grid: { borderColor: '#f0f0f0', strokeDashArray: 3, yaxis: { lines: { show: true } }, xaxis: { lines: { show: false } } },
                            legend: {
                                position: 'top',
                                horizontalAlign: 'left',
                                fontFamily: 'Open Sans, sans-serif',
                                fontSize: '11px',
                                markers: { size: 5, strokeWidth: 0 },
                                itemMargin: { horizontal: 10 },
                            },
                            tooltip: {
                                y: {
                                    formatter: v => (v === null || v === undefined || isNaN(v)) ? 'N/A' : formatChartNumber(v, 2)
                                },
                                style: { fontFamily: 'Open Sans, sans-serif', fontSize: '11px' }
                            },
                            dataLabels: { enabled: false },
                        }}
                        series={displayY.slice(0, 6)}
                        type={chartType}
                        height={300}
                        width="100%"
                    />
                    <YearRangeSlider
                        years={availableYears}
                        fromYear={fromYear}
                        toYear={toYear}
                        onChange={(from, to) => {
                            setYearRange('custom')
                            setFromYear(String(from))
                            setToYear(String(to))
                        }}
                        disabled={availableYears.length === 0}
                    />
                </div>
            ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#737373', fontSize: 13 }}>
                    No data available for the selected unit
                </div>
            )}

            {/* ── Country tags ── */}
            {selectedAvailableCountries.length > 0 && (
                <div className="sc-countries">
                    {selectedAvailableCountries.map(c => (
                        <span key={c} className="sc-country-tag">{c}</span>
                    ))}
                </div>
            )}

            {/* ── Info section: metadata strip + collapsible detail ── */}
            <div className="sc-info">
                {/* Always-visible metadata strip */}
                <div className="sc-meta-grid">
                    {minDate && maxDate && (
                        <div className="sc-meta-section">
                            <div className="sc-meta-label">Timeframe</div>
                            <div className="sc-meta-value sc-meta-mono">{minDate} – {maxDate}</div>
                        </div>
                    )}
                    {meta.total_countries_available != null && (
                        <div className="sc-meta-section">
                            <div className="sc-meta-label">Countries</div>
                            <div className="sc-meta-value">{meta.total_countries_available} available</div>
                        </div>
                    )}
                    {meta.updated && (
                        <div className="sc-meta-section">
                            <div className="sc-meta-label">Last updated</div>
                            <div className="sc-meta-value sc-meta-mono">
                                {String(meta.updated).split('T')[0]}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sources row */}
                {sourcesList.length > 0 && (
                    <div className="sc-sources-row">
                        <span className="sc-sources-label">Sources</span>
                        <div className="sc-sources-chips">
                            {sourcesList.map((src, i) => (
                                <span key={i} className="sc-source-chip">{src}</span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Collapsible header – notes-section style */}
                {(definitionValue || notesList.length > 0 || allAvailableCountries) && (
                    <>
                        <div className="sc-info-header" onClick={() => setIsExpanded(!isExpanded)}>
                            <div className="sc-info-header-left">
                                <span className="sc-info-icon">📖</span>
                                <span className="sc-info-title">Definition & Notes</span>
                                {notesList.length > 0 && (
                                    <span className="sc-info-count">
                                        {notesList.length} {notesList.length === 1 ? 'note' : 'notes'}
                                    </span>
                                )}
                            </div>
                            <span className={`sc-info-chevron${isExpanded ? ' open' : ''}`}>▼</span>
                        </div>

                        <div className={`sc-info-body${isExpanded ? ' open' : ''}`}>
                            <div className="sc-info-content">
                                {/* Definition — labeled with indicator + unit */}
                                {definitionValue && (
                                    <div className="sc-def-item">
                                        <div className="sc-def-term">
                                            {meta.indicator}
                                            {unitValue && (
                                                <span className="sc-def-unit-tag">{unitValue}</span>
                                            )}
                                        </div>
                                        <div className="sc-def-text">{definitionValue}</div>
                                    </div>
                                )}

                                {/* Notes — one sc-note-item per note */}
                                {notesList.length > 0 && (
                                    <div className="sc-def-item">
                                        <div className="sc-def-term sc-def-term-notes">
                                            Notes
                                        </div>
                                        {notesList.map((note, i) => {
                                            const noteCountries = getNoteCountries(note)
                                            return (
                                                <div key={i} className="sc-note-item">
                                                    {noteCountries.length > 0 && (
                                                        <div className="sc-note-flags">
                                                            {noteCountries.map(country => (
                                                                <NoteCountryFlag key={country} country={country} />
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className="sc-note-text">{note}</div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}

                                {/* All available countries */}
                                {allAvailableCountries && (
                                    <div className="sc-def-item">
                                        <div className="sc-def-term">All available countries</div>
                                        <div className="sc-def-text">{allAvailableCountries}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Modales */}
            {viewerModal && (
                <ViewerModal meta={finalMeta} closeCallback={closeViewerHandler} />
            )}

            {addFolderModal && (
                <AddFolderModal closeCallback={closeFolderHandler} meta={metaSender} folders={folderOptions} />
            )}
            {messageModal && (
                <MessageModal closeCallback={closeMessageHandler} title={modalTitle} message={modalMessage} />
            )}

            {loginModal && (
                <LoginModal
                    closeCallback={() => { setLoginModal(false) }}
                    registerCallback={e => { setLoginModal(false); setRegisterModal(true) }}
                    forgotCallback={e => { setLoginModal(false); setForgotModal(true) }}
                    isViewer={true} />
            )}

            {registerModal && (
                <RegisterModal closeCallback={() => { setRegisterModal(false) }} loginCallback={e => { setRegisterModal(false); setLoginModal(true) }} />
            )}

            {forgotModal && (
                <ForgotModal closeCallback={() => { setForgotModal(false) }} />
            )}
        </div>
    )
}

export default SearchCard
